<template>
  <!-- Variante linha vazia: unidade aberta sem escalas (a row já chega com o depth do filho). -->
  <div
    v-if="row.kind === 'empty'"
    data-testid="permission-tree-empty-row"
    :data-depth="row.depth"
    class="tree-row flex min-h-[44px] items-center gap-2.5 px-2"
    :style="{ '--tree-depth': row.depth }"
  >
    <div v-if="row.depth > 0" class="tree-indent relative flex-shrink-0 self-stretch">
      <span class="tree-guide" aria-hidden="true" />
    </div>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      class="h-[18px] w-[18px] flex-shrink-0 text-gray-400"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path :d="mdiCalendarRemove" fill="currentColor" />
    </svg>
    <span class="text-left text-[13px] text-gray-400">{{ emptyText }}</span>
  </div>

  <div
    v-else-if="node"
    role="treeitem"
    data-testid="permission-tree-node"
    :data-depth="row.depth"
    :data-node-type="node.resource_type"
    :data-node-id="node.resource_id"
    :aria-level="row.depth + 1"
    :aria-expanded="row.hasChildren ? Boolean(row.open) : undefined"
    class="tree-row flex min-h-[56px] items-center gap-2.5 rounded-lg px-2 hover:bg-gray-50"
    :class="[row.hasChildren ? 'cursor-pointer' : '', row.dimmed ? 'opacity-50' : '']"
    :style="{ '--tree-depth': row.depth }"
    @click="handleToggle"
  >
    <div v-if="row.depth > 0" class="tree-indent relative flex-shrink-0 self-stretch">
      <span class="tree-guide" aria-hidden="true" />
    </div>

    <svg
      v-if="row.hasChildren"
      data-testid="tree-row-chevron"
      xmlns="http://www.w3.org/2000/svg"
      class="h-[22px] w-[22px] flex-shrink-0 text-gray-500 transition-transform"
      :class="row.open ? 'rotate-90' : ''"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path :d="mdiChevronRight" fill="currentColor" />
    </svg>
    <span v-else class="w-6 flex-shrink-0" aria-hidden="true" />

    <div
      class="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-lg"
      :class="typeStyle.container"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-[19px] w-[19px]"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path :d="typeStyle.icon" fill="currentColor" />
      </svg>
    </div>

    <div class="min-w-0 flex-1 text-left">
      <p class="m-0 truncate text-left text-sm" :class="nameClass">
        {{ node.resource_name }}
        <v-tooltip activator="parent" location="top">{{ nameTooltip }}</v-tooltip>
      </p>
      <p data-testid="tree-row-meta" class="m-0 flex items-center gap-1 text-xs text-gray-400">
        <span>{{ typeLabel }}</span>
        <span aria-hidden="true">·</span>
        <span class="font-mono text-[11px]">ID {{ node.resource_id }}</span>
        <template v-if="summary">
          <span aria-hidden="true">·</span>
          <span class="truncate">{{ summary }}</span>
        </template>
      </p>
    </div>

    <span
      class="whitespace-nowrap rounded-md bg-gray-100 px-2.5 py-1 text-[13px] text-gray-700"
      data-testid="tree-row-relation-chip"
    >
      {{ getRelationLabel(node.effective_relation) }}
      <v-tooltip activator="parent" location="top">
        <div>Efetiva: {{ getRelationLabel(node.effective_relation) }}</div>
        <div v-if="effectiveGrantors">Concedida por: {{ effectiveGrantors }}</div>
      </v-tooltip>
    </span>

    <div
      v-if="origins.length > 0"
      class="flex min-w-[124px] flex-shrink-0 items-center justify-end gap-1"
    >
      <permission-origin-badge
        v-for="(origin, index) in visibleOrigins"
        :key="`${row.key}-origin-${index}`"
        :origin="origin"
      />
      <span
        v-if="overflowOrigins.length > 0"
        data-testid="tree-row-origin-overflow"
        class="inline-flex items-center whitespace-nowrap rounded-md border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700"
      >
        +{{ overflowOrigins.length }}
        <v-tooltip activator="parent" location="top">
          <div v-for="(origin, index) in overflowOrigins" :key="`${row.key}-overflow-${index}`">
            {{ getOriginTooltip(origin) }}
          </div>
        </v-tooltip>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  mdiCalendarMonth,
  mdiCalendarRemove,
  mdiChevronRight,
  mdiDomain,
  mdiHospitalBuilding
} from '@mdi/js'

import { ResourceType } from '@/Network/Types/Responses/PermissionResponses'
import { getRelationLabel, getResourceTypeLabel } from '@/constants/authorizationLabels'

import type { FlatTreeRow } from '../flattenTree'
import { getOriginName, getOriginTooltip } from '../utils'
import PermissionOriginBadge from './PermissionOriginBadge.vue'

/** Badges de origem visíveis por row; o excedente vira o chip "+N" com tooltip. */
const MAX_VISIBLE_ORIGIN_BADGES = 2

const props = defineProps<{ row: FlatTreeRow }>()

const emit = defineEmits<{ toggle: [key: string] }>()

const node = computed(() => props.row.node)

const typeLabel = computed(() => getResourceTypeLabel(node.value?.resource_type))

const TYPE_STYLES: Partial<Record<ResourceType, { icon: string; container: string }>> = {
  [ResourceType.Company]: { icon: mdiDomain, container: 'bg-primary5 text-primary30' },
  [ResourceType.HealthUnit]: { icon: mdiHospitalBuilding, container: 'bg-gray-100 text-gray-700' },
  [ResourceType.Schedule]: { icon: mdiCalendarMonth, container: 'bg-gray-50 text-gray-500' }
}

const typeStyle = computed(
  () =>
    TYPE_STYLES[node.value?.resource_type ?? ResourceType.Company] ?? {
      icon: mdiDomain,
      container: 'bg-gray-100 text-gray-700'
    }
)

const nameClass = computed(() =>
  node.value?.resource_type === ResourceType.Schedule
    ? 'font-medium text-gray-700'
    : 'font-semibold text-gray-800'
)

const nameTooltip = computed(() => {
  const current = node.value
  if (!current) return ''
  return `${current.resource_name} · ${typeLabel.value} · ID ${current.resource_id}`
})

/** Origens do nó na ordem do backend (direct primeiro, depois grupos por nome). */
const origins = computed(() => props.row.origins ?? [])

const visibleOrigins = computed(() => origins.value.slice(0, MAX_VISIBLE_ORIGIN_BADGES))

const overflowOrigins = computed(() => origins.value.slice(MAX_VISIBLE_ORIGIN_BADGES))

/** Origem(ns) que concedem a relation efetiva (2ª linha do tooltip do chip de papel). */
const effectiveGrantors = computed(() => {
  const current = node.value
  if (!current) return ''
  return origins.value
    .filter((origin) => origin.relation === current.effective_relation)
    .map(getOriginName)
    .join(', ')
})

/** Texto da linha vazia varia conforme o nível do pai (empresa sem unidades × unidade sem escalas). */
const emptyText = computed(() => {
  if (props.row.parentType === ResourceType.Company) {
    return 'Nenhuma unidade com permissão nesta empresa'
  }
  return 'Nenhuma escala com permissão nesta unidade'
})

/** Resumo da meta: empresa → nº de unidades; unidade → nº de escalas; escala → nada. */
const summary = computed(() => {
  const current = node.value
  if (!current) return ''
  const childCount = current.children?.length ?? 0
  if (current.resource_type === ResourceType.Company) {
    return childCount === 1 ? '1 unidade' : `${childCount} unidades`
  }
  if (current.resource_type === ResourceType.HealthUnit) {
    if (childCount === 0) return 'sem escalas'
    return childCount === 1 ? '1 escala' : `${childCount} escalas`
  }
  return ''
})

const handleToggle = (): void => {
  if (!props.row.hasChildren) return
  emit('toggle', props.row.key)
}
</script>

<style scoped>
.tree-row {
  --tree-indent-unit: 34px;
}

@media (max-width: 640px) {
  .tree-row {
    --tree-indent-unit: 20px;
  }
}

.tree-indent {
  width: calc(var(--tree-depth, 0) * var(--tree-indent-unit));
}

/* Linha-guia vertical dentro do espaço de indent, ~11px da borda direita. */
.tree-guide {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 11px;
  width: 1px;
  background-color: #e5e7eb;
}
</style>
